import { BaseComponent } from './base-component';
import { PageService } from './page-service';
import { Logger } from './logger';
import { Retry, Retryable, RetryFailure } from './retry';
import { makeStateKey } from '@angular/platform-browser';
import { NavigationStart } from '@angular/router';
import { inject } from '@angular/core';

export function Page(name : string) {
    return (target : any) => {
        Reflect.defineMetadata('page:id', name, target);
    }
}

export function Transfer() {
    return (target : any, propertyKey : string) => {
        Reflect.defineMetadata('state-transfer:enabled', true, target, propertyKey);
    }
}

export interface StandardMeta {
    description? : string;
    keywords? : string;
    author? : string;
}

/**
 * Represents a component which is a page (or dialog). Such components are singletons, and are 
 * typically the center component of a page. Implements server-to-browser state transfer.
 */
export class PageComponent extends BaseComponent {
    private __pageService = inject(PageService);

    public async handleNotFound<T>(promise : Promise<T>): Promise<T> {
        let result : T;
        try {
            return await promise;
        } catch (e) {
            if (e.error == 'not-found') {
                this.__pageService.redirection.go(`/404/${this.__pageService.router.routerState.snapshot.url}`);
                return null;
            }

            throw e;
        }
    }

    protected get shell() { return this.__pageService.shell; }
    protected get router() { return this.__pageService.router; }

    restoreState(data : any) {
        if (!data)
            return;
        
        this._hasServerState = true;
        for (let key of Object.keys(data)) {
            this[key] = data[key];
        }
    }

    /**
     * Whether a load error has been triggered or not. Used by <load-guard> to display an error when page data cannot be retrieved from the 
     * API service.
     */
    public loadError : boolean = false;

    /**
     * Load data with the standard page data retry policy, including triggering `loadError` in the case 
     * where the data cannot be obtained due to a typically retryable reason.
     * 
     * @param f 
     * @param onRetry 
     */
    protected async loadData<T>(f : Retryable<T>, onRetry = null): Promise<T> {
        try {
            return await Retry.standard(f, onRetry);
        } catch (e) {
            if (e instanceof RetryFailure)
                this.onLoadError(e);
            throw e;
        }
    }

    /**
     * Call this when an error occurs while loading data from the API. This will trigger any <load-guard> elements on the page to display an error.
     * @param e 
     */
    protected onLoadError(e) {
        Logger.error(`Error loading data for page ${this.constructor.name}`, e);
        this.loadError = true;
    }

    public set pageTitle(value : string) {
        if (!this.__pageService.router || !this.__pageService.router.routerState)
            return;
        
        let route = this.__pageService.router.routerState.snapshot.root.firstChild;

        if (route.component !== this.constructor) {
            console.warn(`Skipping set page title to '${value}': Component is not the active route.`);
            return;
        }

        if (value === '')
            this.__pageService.title.setTitle(`TYT Network - The Largest Online News Network in the World`);
        else
            this.__pageService.title.setTitle(`${value} - TYT Network`);
    }

    public get meta() {
        return this.__pageService.meta;
    }

    public setMeta(meta : StandardMeta) {
        if (meta.description) this.meta.updateTag({ content: meta.description }, 'name=description');
        if (meta.keywords) this.meta.updateTag({ content: meta.keywords }, 'name=keywords');
        if (meta.author) this.meta.updateTag({ content: meta.author }, 'name=author');
    }

    protected addHeadLink(rel : string, type : string, href : string, title : string = null) {
        let link = this.__pageService.head.addLink(rel, type, href, title);
        let self = this;

        this.subscribe(<any>{
            subscribe(...args) {
                return {
                    unsubscribe() {
                        self.__pageService.head.removeLink(link);
                    }
                }
            }
        }, () => {});
    }

    getState() {
        let state = {};

        for (let key of Object.keys(this)) {
            let value = this[key];

            if (typeof value === 'function')
                continue;
            
            let allowed = Reflect.getMetadata('state-transfer:enabled', this, key);

            if (allowed)
                state[key] = value;
        }

        return state;
    }

    private _hasServerState = false;

    get hasServerState() {
        return this._hasServerState;
    }

    /**
     * Override this to rename the page component based on an input 
     */
    get pageComponentId() {
        return undefined;
    }

    fireInit() {
        
        let fallback = true;
        let pageId : string = this.pageComponentId || Reflect.getMetadata('page:id', this) || this.constructor.name;
        let firstLoad = false;

        if (this.__pageService) {
            this.subscribe(this.__pageService.router.events, ev => {
                if (ev instanceof NavigationStart) {
                    if (!firstLoad) {
                        this._hasServerState = false;
                    } 
                    firstLoad = false;
                }
            });
        }
        
        if (this.__pageService && pageId) {
            if (this.__pageService.stateTransfer) {
                if (typeof window === 'undefined') {
                    // Server side

                    if (pageId.length <= 1) {
                        pageId = 'main';
                    }

                    this.__pageService.stateTransfer.set(makeStateKey<any>(pageId), { works: true });
                    this.__pageService.zone.onStable.subscribe(() => {
                        this.__pageService.stateTransfer.set(makeStateKey<any>(pageId), this.getState())
                    });
                } else {
                    // Client side 
                    let data = this.__pageService.stateTransfer.get(makeStateKey<any>(pageId), null);
                    if (!data) {
                        let originalPageId = pageId;
                        pageId = 'main';
                        data = this.__pageService.stateTransfer.get(makeStateKey<any>(pageId), null);
                    }

                    if (data) {
                        console.dir(data);
                        this.__pageService.stateTransfer.set(makeStateKey<any>(pageId), null);
                    }

                    this.restoreState(data);
                }
            } else {
                //console.log('[Page] Server state transfer service not available.');
            }
        }

        super.fireInit();
    }
}